import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import './CategoryManagement.css';

interface CategoryManagementProps {
  user: any;
}

interface Category {
  categoryId: string;
  enterpriseId: string;
  name: string;
  description: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  parentCategoryId?: string;
  children?: Category[];
  createdAt: string;
  updatedAt: string;
}

// Component to render a single category with its children
interface CategoryCardProps {
  category: Category;
  onEdit: (category: Category) => void;
  onDelete: (categoryId: string) => void;
  level?: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onEdit, onDelete, level = 0 }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = category.children && category.children.length > 0;
  
  return (
    <div className={`category-card-wrapper ${level > 0 ? 'subcategory' : ''}`} style={{ marginLeft: `${level * 24}px` }}>
      <div className={`category-card ${!category.isActive ? 'inactive' : ''}`}>
        <div className="category-header-card">
          {hasChildren && (
            <button 
              className="expand-toggle"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? '▼' : '▶'}
            </button>
          )}
          <div className="category-color-indicator" style={{ backgroundColor: category.color }}></div>
          <div className="category-info">
            <h3>
              {category.name}
              {level > 0 && <span className="subcategory-badge">Subcategory</span>}
            </h3>
            <p>{category.description || 'No description'}</p>
          </div>
        </div>
        
        <div className="category-meta">
          <span className="sort-order">Order: {category.sortOrder}</span>
          {!category.isActive && <span className="badge badge-inactive">Inactive</span>}
        </div>

        <div className="category-actions">
          <button
            onClick={() => onEdit(category)}
            className="btn-small btn-secondary"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(category.categoryId)}
            className="btn-small btn-secondary"
          >
            Delete
          </button>
        </div>
      </div>
      
      {hasChildren && expanded && (
        <div className="subcategories">
          {category.children!.map(child => (
            <CategoryCard
              key={child.categoryId}
              category={child}
              onEdit={onEdit}
              onDelete={onDelete}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryManagement: React.FC<CategoryManagementProps> = ({ user }) => {
  const { enterpriseId: scopeEnterpriseId } = useScope();
  const [categories, setCategories] = useState<Category[]>([]);
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState('');
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);

  // Follow the header enterprise selector until the user picks locally.
  useEffect(() => {
    if (userPickedEnterprise || !scopeEnterpriseId) return;
    if (selectedEnterprise === scopeEnterpriseId) return;
    if (enterprises.length > 0 && !enterprises.some((e: any) => e.enterpriseId === scopeEnterpriseId)) return;
    setSelectedEnterprise(scopeEnterpriseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise, enterprises]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6366f1',
    sortOrder: 0,
    isActive: true,
    parentCategoryId: ''
  });

  const { getIdToken, session } = useAuth();
  const userRole = getUserRole(user, session);
  const isSystemAdmin = hasPermission(user, 'canManageMenuItems', session);

  // Organize categories into hierarchical structure
  const organizeCategories = (categories: Category[]): Category[] => {
    const categoryMap = new Map<string, Category>();
    const rootCategories: Category[] = [];

    // First pass: create map and initialize children arrays
    categories.forEach(cat => {
      categoryMap.set(cat.categoryId, { ...cat, children: [] });
    });

    // Second pass: build hierarchy
    categories.forEach(cat => {
      const category = categoryMap.get(cat.categoryId)!;
      if (cat.parentCategoryId) {
        const parent = categoryMap.get(cat.parentCategoryId);
        if (parent) {
          parent.children!.push(category);
        } else {
          // If parent not found, treat as root
          rootCategories.push(category);
        }
      } else {
        rootCategories.push(category);
      }
    });

    // Sort categories and their children by sortOrder
    const sortCategories = (cats: Category[]): Category[] => {
      return cats.sort((a, b) => a.sortOrder - b.sortOrder).map(cat => ({
        ...cat,
        children: cat.children ? sortCategories(cat.children) : []
      }));
    };

    return sortCategories(rootCategories);
  };

  useEffect(() => {
    loadEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterprise) {
      loadCategories();
    }
  }, [selectedEnterprise]);

  const loadEnterprises = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.enterprises());
      const data = await response.json();
      
      if (response.ok) {
        // Handle different response structures like MenuItemAssignment
        let enterpriseList = [];
        if (Array.isArray(data)) {
          // Direct array response
          enterpriseList = data;
        } else if (data.success && data.data) {
          // Wrapped response with success flag
          enterpriseList = data.data;
        } else if (data.data && Array.isArray(data.data)) {
          // Wrapped response without success flag
          enterpriseList = data.data;
        }
        
        console.log('Enterprises loaded:', enterpriseList);
        setEnterprises(enterpriseList || []);
        // Auto-select: prefer header scope if present, else first available.
        if (enterpriseList?.length > 0) {
          const preferred = scopeEnterpriseId && enterpriseList.some((e: any) => e.enterpriseId === scopeEnterpriseId)
            ? scopeEnterpriseId
            : enterpriseList[0].enterpriseId;
          setSelectedEnterprise(preferred);
        }
      } else {
        console.error('API Error:', data);
        alert(`Failed to load enterprises: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      alert('Failed to load enterprises. Please check your connection and try again.');
    }
  };

  const loadCategories = async () => {
    if (!selectedEnterprise) return;
    
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.categories(selectedEnterprise));
      
      const data = await response.json();
      console.log('Categories API response:', data);
      
      if (response.ok) {
        // Handle different response structures
        let categoriesArray = [];
        if (Array.isArray(data)) {
          categoriesArray = data;
        } else if (data.success && data.data && Array.isArray(data.data)) {
          categoriesArray = data.data;
        } else if (data.data && Array.isArray(data.data)) {
          categoriesArray = data.data;
        } else if (data.categories && Array.isArray(data.categories)) {
          categoriesArray = data.categories;
        }
        
        // Sort categories by sortOrder
        const sortedCategories = categoriesArray.sort((a: Category, b: Category) => a.sortOrder - b.sortOrder);
        setCategories(sortedCategories);
      } else {
        console.error('API Error:', data);
        setCategories([]);
        // Don't show error alert for 404 - categories endpoint might not exist yet
        if (response.status !== 404) {
          alert(`Failed to load categories: ${data.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
      alert('Failed to load categories. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (formData: any) => {
    try {
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.createCategory(selectedEnterprise), {
        method: 'POST',
        headers,
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowForm(false);
        resetForm();
        loadCategories();
      } else {
        const errorData = await response.json();
        if (response.status === 404) {
          alert('Categories endpoint not implemented yet. Please contact support.');
        } else {
          alert(`Failed to create category: ${errorData.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category');
    }
  };

  const handleUpdateCategory = async (formData: any) => {
    if (!editingCategory || !selectedEnterprise) return;

    try {
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      // Prepare data with enterprise ID and proper parent category handling
      const submitData = {
        ...formData,
        enterpriseId: selectedEnterprise,
        parentCategoryId: formData.parentCategoryId || null
      };
      
      console.log('Updating category with data:', submitData);
      
      const response = await fetch(API_ENDPOINTS.updateCategory(editingCategory.categoryId), {
        method: 'PUT',
        headers,
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        console.log('Category updated successfully');
        setShowForm(false);
        setEditingCategory(null);
        resetForm();
        loadCategories();
      } else {
        const errorData = await response.json();
        console.error('Update category error response:', errorData);
        console.error('Response status:', response.status);
        alert(`Failed to update category: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Error updating category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.deleteCategory(categoryId), {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        loadCategories();
      } else {
        alert('Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      color: category.color,
      sortOrder: category.sortOrder,
      isActive: category.isActive,
      parentCategoryId: category.parentCategoryId || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#6366f1',
      sortOrder: 0,
      isActive: true,
      parentCategoryId: ''
    });
  };


  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }
    
    if (!selectedEnterprise) {
      alert('Please select an enterprise first');
      return;
    }
    
    if (editingCategory) {
      handleUpdateCategory(formData);
    } else {
      handleCreateCategory(formData);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    resetForm();
  };

  return (
    <>
    <div className="category-management">
      <div className="category-header">
        <div>
          <p>Create and manage menu categories for your enterprises</p>
        </div>
        <button 
          onClick={() => setShowForm(true)} 
          className="btn-primary"
          disabled={!selectedEnterprise}
        >
          Add Category
        </button>
      </div>

      {/* Enterprise selection lives in the header strip — no in-page picker. */}

      {/* Categories List */}
      <div className="category-content">
        {selectedEnterprise && (
          <>
            {loading ? (
              <div className="loading-state">
                <div className="loading-skeleton"></div>
                <p>Loading categories...</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="empty-state">
                <h3>No categories found</h3>
                <button onClick={() => setShowForm(true)} className="btn-primary">
                  Create First Category
                </button>
              </div>
            ) : (
              <div className="categories-tree">
                {organizeCategories(categories).map(category => (
                  <CategoryCard
                    key={category.categoryId}
                    category={category}
                    onEdit={handleEdit}
                    onDelete={handleDeleteCategory}
                    level={0}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

    </div>
    
    {/* Modal Portal - Render outside main component */}
    {showForm && ReactDOM.createPortal(
      <div 
        className="modal-overlay" 
        onClick={handleCancel}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          backdropFilter: 'blur(4px)',
          visibility: 'visible',
          opacity: 1
        }}
        ref={(el) => {
          if (el) {
            // Force styles that were working in debugging
            el.style.setProperty('display', 'flex', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            el.style.setProperty('opacity', '1', 'important');
          }
        }}
      >
        <div 
          className="modal-content" 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            margin: '1rem'
          }}
        >
          <div className="modal-header">
            <h2>{editingCategory ? 'Edit Category' : 'Create Category'}</h2>
            <button onClick={handleCancel} className="btn-close">×</button>
          </div>
          
          <form onSubmit={handleFormSubmit} className="category-form">
            <div className="form-group">
              <label htmlFor="name">Category Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-textarea"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="parentCategory">Parent Category (Optional)</label>
              <select
                id="parentCategory"
                value={formData.parentCategoryId}
                onChange={(e) => setFormData({ ...formData, parentCategoryId: e.target.value })}
                className="form-input"
              >
                <option value="">No Parent (Top Level)</option>
                {categories
                  .filter(cat => cat.categoryId !== editingCategory?.categoryId)
                  .map(cat => (
                    <option key={cat.categoryId} value={cat.categoryId}>
                      {cat.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="color">Color</label>
                <input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="form-color"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sortOrder">Sort Order</label>
                <input
                  id="sortOrder"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active Category
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    )}
  </>
  );
};

export default CategoryManagement;